/**
 * Client-side analytics: posts events to the `analytics.trackEvent` API with
 * session and feature context.
 *
 * A session key groups the events from one page load. It is minted when the
 * bundle loads and replaced on the first event more than an hour later, so it
 * is neither a visit nor a person: navigating within the app keeps the same
 * key, while a reload or a link that opens a new tab starts a fresh one.
 *
 * `trackEvent` enriches every call with that key, the active
 * silo/structure/instance, the enabled-feature map for a structure that asks
 * to record one, admin status, and the device info captured when the session
 * started. The public API is intentionally non-async (returns `void`):
 * analytics is fire-and-forget by design, and encoding that in the signature
 * avoids the need for callers to wrap every call in `fireAndForget`.
 *
 * It is deliberately resilient. It takes a null `Datastore`, so a caller with
 * no instance mounted still records an event, reading the silo off the URL —
 * boot-time perf marks arrive that way. It swallows its own exceptions, since
 * a logging failure must never feed back into more logging, and it goes
 * through `data/analytics`, which holds an API client of its own, for the same
 * reason. Two cases record nothing: a `Datastore` in test mode, so a demo or a
 * story driving real UI stays out of the log, and a page with no silo in its
 * URL at all, which is only ever a redirect or a not-found.
 */

import type { TrackEventInput } from '@repo/shared/orpc/procedures/analytics';
import { trackEventAsync } from 'data/analytics';
import { getDeviceInfo } from 'platform-specific/deviceinfo';
import { getSiloKeyFromWindowUrl } from 'platform-specific/url';
import { useEffect, useState } from 'react';

import type { Datastore } from './datastore';
import { getEnabledFeatures } from './features';
import { fireAndForget } from './hooks';

const hourMillis = 60 * 60 * 1000;

let globalSessionKey: string | null = null;
let globalSessionTime: number | null = null;
let globalDeviceInfo: TrackEventInput['deviceInfo'] = null;

/**
 * Round-trips event params through JSON so they satisfy the contract's
 * JSON-record schema, which refuses anything that is not JSON. Callers pass
 * whatever object is at hand, so this drops `undefined` values and stands a
 * marker in for a circular one rather than letting a param nobody meant to be
 * load-bearing cost the whole event.
 */
function toLoggableParams(params: Record<string, any> | undefined): TrackEventInput['params'] {
    try {
        return JSON.parse(JSON.stringify(params ?? {}));
    } catch (e) {
        return { error: 'Non-serializable object' };
    }
}

/**
 * Where, for whom, and on what an event happened: the fields the app fills in
 * around an event, as opposed to the event's own params.
 */
export type EventContext = Omit<TrackEventInput, 'eventType' | 'params'>;

/**
 * The part of an event's context that a datastore's instance decides. The
 * feature map is empty unless the structure asks for one with `logsFeatures`.
 */
export function getDatastoreEventContext(datastore: Datastore) {
    const siloKey = datastore.getSiloKey();
    const structureKey = datastore.getStructureKey();
    const structureModule = datastore.getStructureModule();
    const activeFeatures = datastore.getActiveFeatures();
    return {
        siloKey,
        structureKey,
        instanceKey: datastore.getInstanceKey(),
        enabledFeatures: structureModule.logsFeatures ? getEnabledFeatures({ siloKey, structureKey, structureModule, activeFeatures }) : {},
    };
}

/**
 * Records an event. Its context comes from the datastore, with any fields of
 * `context` taking their place.
 */
export function trackEvent(
    datastore: Datastore | null,
    eventType: string,
    params?: Record<string, any>,
    context?: Partial<EventContext> | null,
): void {
    fireAndForget(trackEventInternalAsync(datastore, eventType, params, context));
}

async function trackEventInternalAsync(
    datastore: Datastore | null,
    eventType: string,
    params?: Record<string, any>,
    context?: Partial<EventContext> | null,
) {
    try {
        if (datastore?.getIsTestMode()) {
            return;
        }

        const siloKey = datastore?.getSiloKey() || getSiloKeyFromWindowUrl();
        if (!siloKey) {
            return;
        }

        const roles = datastore?.sessionData?.roles;

        if (!globalSessionKey || !globalSessionTime || Date.now() - globalSessionTime > hourMillis) {
            globalSessionKey = crypto.randomUUID();
            globalSessionTime = Date.now();
            globalDeviceInfo = getDeviceInfo();
        }

        const eventContext: EventContext = {
            structureKey: null,
            instanceKey: null,
            enabledFeatures: {},
            ...(datastore ? getDatastoreEventContext(datastore) : {}),
            siloKey,
            isAdmin: roles ? roles.length > 0 : null,
            sessionKey: globalSessionKey,
            deviceInfo: globalDeviceInfo,
            ...context,
        };
        await trackEventAsync({
            eventType,
            params: toLoggableParams(params),
            ...eventContext,
        });
    } catch (e) {
        // Exceptions during logging need to be swallowed, otherwise we'll get
        // stuck in an infinite loop
        console.error('Error logging event', e);
    }
}

export function useStartTime() {
    const [startTime, setStartTime] = useState(Date.now());
    useEffect(() => {
        setStartTime(Date.now());
    }, []);
    return startTime;
}
